/* Roles */
import { ActionEnum, Capability, LakehouseTable, User } from '@vendia/management-api-types'
import { Acl } from 'src/utils/csv-uploader/csv-uploader'

// This is rad, should use it more, shows the final result type
//  in IDE rather than GraphqlFetchResponse<...other junk...>
// https://www.totaltypescript.com/concepts/the-prettify-helper
export type Prettify<T> = {
  [K in keyof T]: T[K]
} & {} // eslint-disable-line @typescript-eslint/ban-types

export type Actions = keyof typeof ActionEnum

export enum ActionCategory {
  USER = 'USER',
  UNI = 'UNI',
  ORG = 'ORG',
  DATA = 'DATA',
}
export type ActionCategories = keyof typeof ActionCategory

export type ActionRepresentation = `Action.${Actions}`

/* Resources */

export type NameResourceString = `${string}@${string}.${string}.${string}`

export type UniResourceString =
  | `${string}.${string}.${string}.${string}`
  | `${string}.${string}.${string}.${string}#${string}`

export type VendiaResourceString =
  | `${string}`
  | `${string}/${string}`
  | `${string}/${string}/${string}`
  | `${string}/${string}/${string}/${string}`

export type NameResourceRepresentation = `NameResource(${NameResourceString})`

export type UniResourceRepresentation = `UniResource(${UniResourceString})`

export type OrgResourceRepresentation = `OrganizationResource(${string})`

export type VendiaResourceRepresentation = `VendiaResource(${VendiaResourceString})`

export interface NameResource {
  name: string
  subdomain: string
  domain: string
  ext: string
}

export interface UniResource {
  name: string
  subdomain: string
  domain: string
  ext: string
  nodeName: string
}

export interface OrgResource {
  orgId: string
}

export interface VendiaResource {
  orgId: string
  uniName: string
  nodeName: string
}

export type Resource = NameResource | UniResource | OrgResource | VendiaResource

export type ResourceRepresentation =
  | NameResourceRepresentation
  | UniResourceRepresentation
  | OrgResourceRepresentation
  | VendiaResourceRepresentation

export type RoleRepresentation = {
  [key in ActionRepresentation]?: ResourceRepresentation[]
}

export type RoleTemplate = {
  name: string
  description: string | JSX.Element
  capabilities?: Capability[]
}

/* User Types */

export type OrgMember = User & {
  external?: boolean
}

// Sting values need to match back-end feature flags
export enum UniFeatures {
  VENDIA_TABLES_ENABLED = 'vendia_tables_enabled',
  READ_RECEIPTS_ENABLED = 'read_receipts_enabled',
  SQL_NODE_LOCAL_CLUSTER = 'sql_node_local_cluster',
  SQL_REMOTE_CLUSTER = 'sql_remote_cluster',
  SQL_NODE_LOCAL_CLUSTER_NEON = 'sql_node_local_cluster_neon',
  ZERO_DOWNTIME_ENABLED = '0dde',
}

// Data plane Table type - parsing tableConfig string into LakehouseTable type fields and
// adding them to the resulting table items
export type DatabaseSourceConfig = {
  tableName?: string
  connectionId?: string
  lastUpdatedColumn?: string
  lastUpdatedColumnFormat?: string
  keyColumns?: string[]
}

export type VendiaSourceConfig = {
  lastUpdatedColumn?: string
  lastUpdatedColumnFormat?: string
  keyColumns?: string[]
  joinQuery?: any
  tableName?: string
}

export type UploadSourceConfig = {
  csvColumns?: string[]
  fileName?: string
  csvOptions?: {
    dateFormat?: string
    delimiter?: string
    enclosure?: string
    errorHandling?: string
    header?: boolean
    timeFormat?: string
    timestampFormat?: string
    whitespace?: string
  }
}

export type S3SourceConfig = UploadSourceConfig & {
  connectionId?: string
}

export type Table = LakehouseTable & {
  _id: string
  _owner: string
  _acl: Acl[]
  name: string
  source?: {
    definition?: {
      snowflake?: DatabaseSourceConfig
      postgres?: DatabaseSourceConfig
      cloudera?: DatabaseSourceConfig
      vendia?: VendiaSourceConfig
      upload?: UploadSourceConfig
      s3?: S3SourceConfig
      mysql?: DatabaseSourceConfig
      databricks?: DatabaseSourceConfig
      bigquery?: DatabaseSourceConfig
    }
  }
  policies: any
}

// TODO: use management API types
export interface Actor {
  id: string
  type:
    | 'API_KEY'
    | 'API_CREDENTIALS'
    | 'JWT_EXTERNAL'
    | 'SMART_CONTRACT'
    | 'VENDIA_USER'
    | 'VENDIA_SYSTEM'
    | 'VENDIA_INTERNAL'
  roleId: string
  roleName: string
  roleVersion: string
  contractTaskId: string
}

// Copied from vendia/vendia, src/share/share_backend/modules_ts/libs/models/src/read-receipts.ts
export interface ReadReceiptMessage {
  actor: {
    id: string
    type:
      | 'API_KEY'
      | 'API_CREDENTIALS'
      | 'SMART_CONTRACT'
      | 'VENDIA_USER'
      | 'VENDIA_SYSTEM'
      | 'VENDIA_INTERNAL'
      | 'JWT_EXTERNAL'
    roleId?: string
    roleName?: string
    roleVersion?: string
  }
  clientType: 'VENDIA_UI' | 'VENDIA_CLI' | 'MCP' | 'OTHER'
  // Will be registered client ID for MCP clients
  clientId?: string
  // Should be the time the query request was received
  readTime: string
  orgId?: string
  query?: string
  queryVariables?: string
  queryType?:
    | 'ICEBERG_SQL'
    | 'GRAPHQL'
    | 'FILE_READ'
    | 'FILE_WRITE'
    | 'FILE_CREATE'
    | 'FILE_DELETE'
    | 'API_WRITE'
    | 'API_READ'
    | 'STORAGE_CONNECTION_CREATE'
    | 'STORAGE_CONNECTION_UPDATE'
    | 'STORAGE_CONNECTION_DELETE'
    | 'STORAGE_CONNECTION_LIST'
    | 'STORAGE_CONNECTION_READ'
    | 'MCP_GATEWAY_TOOL_LIST'
    | 'MCP_GATEWAY_TOOL_CALL'
    | 'MCP_TOOL'
  fileSourceType: 'EXTERNAL_S3' | 'VENDIA'
  fileKey: string
  fileSourceId: string
  fileSourceName?: string
  // MCP Gateway fields
  gatewayServerId?: string
  gatewayServerUrl?: string
  gatewayServerName?: string
  gatewayServerToolName?: string
  toolName?: string
  durationMs?: number
  clientName?: string
  mcpSessionId?: string
  status?: 'SUCCESS' | 'FAILED'
  errorMessage?: string
  // PII Governance fields
  piiDetected?: boolean
  piiClassifiers?: string[]
  piiAction?: 'BLOCKED' | 'MASKED' | 'ALLOWED'
}

// Vendia Entity Instance type
export interface VendiaEntityInstanceBase {
  _id: string
  _owner: string
  _acl: Array<Acl>
}

export interface VendiaEntityInstance extends VendiaEntityInstanceBase {
  [key: string]: any
}
